import { useRef, useState } from "react";
import { Camera, Upload, RotateCw, CheckCircle2, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { budgetBandLabel } from "@/lib/budget";

// FragranceBottleScanner — embed in the FragranceCoachScreen result.
// User uploads a photo of any cologne bottle they spotted in-store; the AI
// identifies it, compares to what we recommended, and gives an expert take.

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("read failed"));
    r.onload  = () => resolve((r.result || "").toString().split(",").pop());
    r.readAsDataURL(file);
  });
}

const VERDICT_COPY = {
  yes_great_choice:         { label: "Great pick", tone: "positive" },
  similar_to_recommended:   { label: "Same lane", tone: "positive" },
  different_but_great:      { label: "Different but solid", tone: "positive" },
  not_recommended_here:     { label: "Not the move for you", tone: "warn" },
  cant_identify:            { label: "Can't fully tell — try a clearer shot", tone: "warn" },
};

export default function FragranceBottleScanner({ previousRecommendation }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handle = async (file, question) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    setPreviewUrl(URL.createObjectURL(file));
    try {
      const image_base64 = await fileToBase64(file);
      const { data } = await api.post("/fragrance/identify", {
        image_base64,
        image_mime: file.type || "image/jpeg",
        previous_recommendation: previousRecommendation || null,
        user_question: question || null,
      });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setPreviewUrl(null);
  };

  return (
    <div className="outfit-section" data-testid="fragrance-bottle-scanner">
      <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
        <Camera size={12} strokeWidth={1.8} /> AT THE COUNTER? SHOW ME ANY BOTTLE
      </div>
      <div className="body-sm" style={{ color: "var(--text-dim)", marginBottom: 12 }}>
        Found something else? Snap it — I&apos;ll tell you whether it&apos;s a great pick, how it compares to what I recommended, and what to expect.
      </div>

      {!result && !busy && (
        <>
          <input ref={camRef}  type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => handle(e.target.files?.[0])} data-testid="frag-scan-camera-input" />
          <input ref={galRef}  type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => handle(e.target.files?.[0])} data-testid="frag-scan-gallery-input" />
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn btn-gold" style={{ flex: 1, justifyContent: "center" }} onClick={() => camRef.current?.click()} data-testid="frag-scan-camera-btn">
              <Camera size={14} /> Take photo
            </button>
            <button type="button" className="btn btn-outline" style={{ flex: 1, justifyContent: "center" }} onClick={() => galRef.current?.click()} data-testid="frag-scan-gallery-btn">
              <Upload size={14} /> From gallery
            </button>
          </div>
        </>
      )}

      {busy && (
        <div className="card" style={{ padding: 18, textAlign: "center" }} data-testid="frag-scan-busy">
          <div className="tiny" style={{ letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--gold)" }}>
            IDENTIFYING…
          </div>
          <div className="body-sm" style={{ marginTop: 8, color: "var(--text-dim)" }}>
            Reading the bottle and comparing to your recommendation
          </div>
        </div>
      )}

      {error && (
        <div className="error-text" data-testid="frag-scan-error">{error}</div>
      )}

      {result && (
        <div data-testid="frag-scan-result">
          {previewUrl && (
            <div style={{ borderRadius: 14, overflow: "hidden", border: "1px solid var(--line)", marginBottom: 12 }}>
              <img src={previewUrl} alt="" style={{ width: "100%", display: "block", maxHeight: 220, objectFit: "cover" }} />
            </div>
          )}

          {/* Verdict pill */}
          {result.verdict && (
            <div
              className={"frag-verdict frag-verdict-" + (VERDICT_COPY[result.verdict]?.tone || "warn")}
              data-testid="frag-scan-verdict"
            >
              {result.verdict === "yes_great_choice" || result.verdict === "similar_to_recommended" || result.verdict === "different_but_great"
                ? <CheckCircle2 size={14} />
                : <AlertCircle size={14} />}
              <span>{VERDICT_COPY[result.verdict]?.label || result.verdict}</span>
            </div>
          )}

          {/* Identified */}
          {result.identified?.name && (
            <div style={{ marginTop: 10 }} data-testid="frag-scan-identified">
              <div className="serif" style={{ fontFamily: "'Fraunces', serif", fontSize: 22, color: "var(--text)", lineHeight: 1.2 }}>
                {result.identified.name}
              </div>
              <div className="tiny" style={{ color: "var(--text-faint)", marginTop: 4 }}>
                {[result.identified.brand, result.identified.concentration, result.identified.year].filter(Boolean).join(" · ")}
              </div>
            </div>
          )}

          {result.verdict_line && (
            <div className="body-sm" style={{ marginTop: 12, color: "var(--text)", fontStyle: "italic" }} data-testid="frag-scan-line">
              &ldquo;{result.verdict_line}&rdquo;
            </div>
          )}

          {result.compare_to_recommendation && (
            <div className="card" style={{ marginTop: 12, padding: 14, borderColor: "var(--gold-line)", background: "var(--gold-soft)" }} data-testid="frag-scan-compare">
              <div className="tiny" style={{ letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--gold)" }}>
                COMPARED TO MY RECOMMENDATION
              </div>
              <div className="body-sm" style={{ marginTop: 6, color: "var(--text)" }}>
                {result.compare_to_recommendation}
              </div>
            </div>
          )}

          {/* Scent profile */}
          {result.scent_profile && (
            <div style={{ marginTop: 14 }} data-testid="frag-scan-profile">
              <div className="eyebrow" style={{ marginBottom: 6 }}>SCENT PROFILE</div>
              {result.scent_profile.family && (
                <div className="body-sm" style={{ color: "var(--gold)", marginBottom: 6, fontWeight: 500 }}>
                  {result.scent_profile.family}
                </div>
              )}
              <div className="tiny" style={{ color: "var(--text-dim)", lineHeight: 1.7 }}>
                <strong>Top:</strong> {(result.scent_profile.top || []).join(", ")}<br/>
                <strong>Heart:</strong> {(result.scent_profile.heart || []).join(", ")}<br/>
                <strong>Base:</strong> {(result.scent_profile.base || []).join(", ")}
              </div>
            </div>
          )}

          {/* Performance */}
          {result.performance && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }} data-testid="frag-scan-perf">
              <div className="card" style={{ padding: 10 }}>
                <div className="tiny" style={{ color: "var(--text-faint)", letterSpacing: "0.18em" }}>LONGEVITY</div>
                <div className="body" style={{ color: "var(--text)", marginTop: 2, textTransform: "capitalize" }}>{result.performance.longevity || "—"}</div>
              </div>
              <div className="card" style={{ padding: 10 }}>
                <div className="tiny" style={{ color: "var(--text-faint)", letterSpacing: "0.18em" }}>PROJECTION</div>
                <div className="body" style={{ color: "var(--text)", marginTop: 2, textTransform: "capitalize" }}>{result.performance.projection || "—"}</div>
              </div>
            </div>
          )}

          {result.value?.value_note && (
            <div className="tiny" style={{ marginTop: 12, color: "var(--text-dim)", lineHeight: 1.5 }} data-testid="frag-scan-value">
              <strong style={{ color: "var(--gold)" }}>Value{(result.value.budget_band || result.value.price_band) ? ` (${budgetBandLabel(result.value.budget_band || result.value.price_band)})` : ""}:</strong> {result.value.value_note}
            </div>
          )}

          <button type="button" className="btn btn-outline" onClick={reset} data-testid="frag-scan-reset" style={{ marginTop: 14, width: "100%", justifyContent: "center" }}>
            <RotateCw size={14} /> Scan another bottle
          </button>
        </div>
      )}
    </div>
  );
}
